/* eslint-disable no-underscore-dangle */
import React from 'react';
import { useDialogStore } from 'hooks/dialogStore/useDialogStore';
import { DIALOG_KEYS } from 'modals/keys';
import {
  FeedbackModalMode,
  FeedbackModalType,
  FeedbackModalContextType,
} from './types';

const FeedbackModalContext = React.createContext<FeedbackModalContextType>(
  {} as any,
);

export const FeedbackModalContextProvider: React.FC = ({ children }) => {
  const [visible, setVisible] = React.useState(false);
  const { addDialog, removeDialog, isDialogVisible, isUsingNewArchitecture } =
    useDialogStore();
  const feedbackUsingNewArch = isUsingNewArchitecture(
    DIALOG_KEYS.FeedbackModal,
  );

  const [listingId, setListingId] = React.useState<string>();
  const [appointmentId, setAppointmentId] = React.useState<string>();
  const [modalType, setModalType] = React.useState<FeedbackModalType>();
  const [modalMode, setModalMode] = React.useState<FeedbackModalMode>();
  const [onClose, setOnClose] = React.useState<() => Promise<void>>();
  const [onSubmit, setOnSubmit] = React.useState<() => Promise<void>>();

  const _resetModal = () => {
    setListingId(undefined);
    setAppointmentId(undefined);
    setModalType(undefined);
    setModalMode(undefined);
    setOnClose(undefined);
    setOnSubmit(undefined);
  };

  const memoizedValue = React.useMemo(() => {
    const openModal: FeedbackModalContextType['openModal'] = options => {
      setListingId(options.listingId);
      setAppointmentId(options.appointmentId);
      setModalType(options.modalType);
      setModalMode(options.modalMode);
      setOnClose(() => options.onClose);
      setOnSubmit(() => options.onSubmit);

      if (feedbackUsingNewArch) {
        addDialog({
          key: DIALOG_KEYS.FeedbackModal,
        });
      }
      setVisible(true);
    };

    const closeModal: FeedbackModalContextType['closeModal'] = async (
      options = {
        resetModal: true,
        fireSubmit: false,
      },
    ) => {
      if (feedbackUsingNewArch) {
        removeDialog(DIALOG_KEYS.FeedbackModal);
      }
      setVisible(false);

      if (options.fireSubmit) await onSubmit();
      if (onClose) await onClose();
      if (options.resetModal) _resetModal();
    };
    return {
      visible,
      openModal,
      closeModal,
      onClose,
      listingId,
      appointmentId,
      modalType,
      modalMode,
    };
  }, [
    feedbackUsingNewArch,
    visible,
    onClose,
    listingId,
    appointmentId,
    modalType,
    modalMode,
    addDialog,
    onSubmit,
    removeDialog,
  ]);

  return (
    <FeedbackModalContext.Provider value={memoizedValue}>
      {children}
    </FeedbackModalContext.Provider>
  );
};

export default FeedbackModalContext;
